import { Component, OnInit, Output, EventEmitter, AfterViewInit, inject } from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {PagesLoadedEvent} from 'ngx-extended-pdf-viewer/lib/events/pages-loaded-event';
import {Observable} from 'rxjs';
import {map, switchMap, finalize, tap, shareReplay} from 'rxjs/operators';
import * as htmlToImage from 'html-to-image';
import {FeatureFlagService, FeatureFlags} from '@core';
import {Location, ArraySort, DropdownItem} from '@shared';

import {
    RoutesService,
    ProblemInstanceService,
    VisitList,
    OfficeInfo,
    EmployeeWorkShift,
    TaskIdInfo, Visit
} from 'planner-shared';

import {EmployeeRouteInfoView} from './models/employee-route-info-view.model';
import jsPDF from 'jspdf';
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: 'app-route-planning-export',
    templateUrl: './route-planning-export.component.html',
    styleUrls: ['./route-planning-export.component.scss'],
    standalone: false
})
export class RoutePlanningExportComponent implements OnInit, AfterViewInit {
    private readonly modalRef = inject(BsModalRef);
    private readonly routesService = inject(RoutesService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly problemInstanceService = inject(ProblemInstanceService);

    public planDate: string;
    public loading: boolean;
    public includeMap: boolean;
    public employeeDropdownItems: DropdownItem[];
    public selectedEmployeeWorkHourId: string;
    public nonAllocatedTasksInfo: TaskIdInfo[];
    public mapsEnabled$: Observable<boolean>;
    public employeesOriginal$: Observable<EmployeeRouteInfoView[]>;
    public employees$: Observable<EmployeeRouteInfoView[]>;
    public routeSolution: Visit[];

    public readonly DefaultSelectedValue = 'all';
    public pdfData: string;
    public totalPageCount = 0;

    @Output() public confirm = new EventEmitter();

    private pdfViewerEventBus: any;
    private footerCanvas: HTMLCanvasElement;
    private footerImage: string;

    constructor() {
        this.employeeDropdownItems = [<DropdownItem>{value: this.DefaultSelectedValue, label: 'ALL'}];
        this.selectedEmployeeWorkHourId = this.DefaultSelectedValue;
    }

    ngOnInit(): void {
        this.loading = true;
        this.mapsEnabled$ = this.featureFlagService.isEnabled(FeatureFlags.Maps);
        this.employeesOriginal$ = this.problemInstanceService.getProblemInstance().pipe(
            untilDestroyed(this),
            tap(result => (this.planDate = result.problemInstance.officeInfo.planDate)),
            switchMap(result =>
                this.routesService.getRoutes().pipe(
                    map(routesResult => {
                        const employees = result.problemInstance.employeeWorkShifts
                            .map(employee =>
                                this.mapEmployeeRouteInfo(
                                    routesResult.routes,
                                    employee,
                                    result.problemInstance.officeInfo
                                )
                            )
                            .filter(employee => employee.tasks && employee.tasks.length > 0);

                        ArraySort.sort(employees, 'employeeName', true);
                        this.employeeDropdownItems.push(
                            ...employees.map(
                                employee =>
                                    <DropdownItem>{
                                        value: employee.workHourId,
                                        label: employee.employeeName
                                    }
                            )
                        );

                        this.nonAllocatedTasksInfo = routesResult.routes?.nonAllocatedTasksInfo;
                        return employees;
                    })
                )
            ),
            finalize(() => this.reloadPreview()),
            shareReplay(1)
        );
        this.problemInstanceService.getProblemInstance()
            .pipe(untilDestroyed(this))
            .subscribe(instance => {
                this.routesService.getRoutes(instance.problemInstance.jobId).subscribe(solution => {
                    this.routeSolution = solution.routes.tasks;
                });
            });
        this.employees$ = this.employeesOriginal$.pipe(
            map(data => JSON.parse(JSON.stringify(data))));
    }

    ngAfterViewInit() {
        const footerElement = document.getElementById('footer');
        htmlToImage.toCanvas(footerElement, {backgroundColor: 'white'})
            .then(footer => {
                this.footerCanvas = footer;
                this.footerImage = footer.toDataURL('image/png', 1.0);
            });
    }

    public dismissModal(): void {
        this.modalRef.hide();
    }

    public pdfViewerPagesLoaded(event: PagesLoadedEvent): void {
        this.pdfViewerEventBus = event.source['eventBus'];
    }

    public print(): void {
        this.pdfViewerEventBus.dispatch('print');
    }

    public reloadPreview(): void {
        this.loading = true;
        if (this.selectedEmployeeWorkHourId === this.DefaultSelectedValue) {
            this.employees$ = this.employeesOriginal$.pipe(
                tap(employees => this.totalPageCount = employees.length),
                map(employees => JSON.parse(JSON.stringify(employees))));
        } else {
            this.employees$ = this.employeesOriginal$.pipe(
                map(employees =>
                    employees.filter(employee => employee.workHourId === this.selectedEmployeeWorkHourId)),
                tap(employees => this.totalPageCount = employees.length)
            );
        }
        setTimeout(
            () => {
                this.captureCanvas();
            },
            500
        );
    }

    private async captureCanvas() {
        const MARGIN_X = 10;
        const MARGIN_Y = 14;

        const A4_HEIGHT = 842;
        const A4_WIDTH = 595;

        const pages = Array.from({length: this.totalPageCount + 1}, (_, i) => `page-${i}`);
        const footerPositionY = 795;

        const pdf = new jsPDF('p', 'pt', 'a4');

        const canvases: HTMLCanvasElement[] = await Promise.all(
            pages.map(id => {
                const element = document.getElementById(id);
                return htmlToImage.toCanvas(element, {backgroundColor: 'white'});
            }));

        canvases.forEach((canvas, index) => {
            const PDF_Width = A4_WIDTH - 2 * MARGIN_X;
            const PDF_Height = (PDF_Width / canvas.width) * canvas.height;
            const footerOffset = PDF_Height - footerPositionY;
            const pageImg = canvas.toDataURL('image/png', 1.0);
            let positionY = MARGIN_Y;
            let heightUnprinted = PDF_Height;
            while (heightUnprinted > 0) {
                pdf.addImage(
                    pageImg,
                    'PNG',
                    MARGIN_X,
                    positionY,
                    PDF_Width,
                    PDF_Height,
                    '',
                    'FAST'
                );

                pdf.addImage(
                    this.footerImage,
                    'PNG',
                    MARGIN_X,
                    footerPositionY,
                    PDF_Width,
                    (PDF_Width / this.footerCanvas.width) * this.footerCanvas.height,
                    '',
                    'FAST'
                );

                pdf.setFillColor(255, 255, 255);
                pdf.rect(0, 0, A4_WIDTH, MARGIN_Y, 'F');
                pdf.rect(0, A4_HEIGHT - MARGIN_Y, A4_WIDTH, MARGIN_Y, 'F');
                heightUnprinted -= PDF_Height - footerOffset;
                positionY -= PDF_Height - footerOffset - MARGIN_Y;

                if (heightUnprinted > 0) {
                    pdf.addPage();
                }
            }
            if (canvases.length !== index + 1) {
                pdf.addPage();
            }
        });
        this.pdfData = URL.createObjectURL(pdf.output('blob'));
        this.loading = false;
    }

    private mapEmployeeRouteInfo(
        routes: VisitList,
        employee: EmployeeWorkShift,
        officeInfo: OfficeInfo
    ): EmployeeRouteInfoView {
        const keyNumbers = routes.routeKeyNumbers.find(route => route.workHourId === employee.workHourId);
        let tasks = routes.tasks.filter(task => task.workHourId === employee.workHourId);

        // Max 25 waypoints can be displayed on map
        if (tasks.length > 25) {
            tasks = tasks.slice(0, 25);
        }

        return <EmployeeRouteInfoView>{
            employeeId: employee.employeeId,
            workHourId: employee.workHourId,
            employeeName: employee.employeeName,
            transport: employee.transport,
            officeLocation: <Location>{
                latitude: officeInfo.latitude,
                longitude: officeInfo.longitude
            },
            keyNumbers,
            tasks
        };
    }
}
